/**
 * ProviderShell — the frame every provider screen hangs in (EP-004P Phase 1).
 *
 * == The refusal states are the main feature ==============================
 *
 * On the day this ships, every provider will hit a refusal. No role assignment
 * has been ratified, no provider is assigned to a facility, and no care
 * assignment exists — so the honest first experience of this portal is being
 * turned away.
 *
 * A generic "Access denied" would send ten people to ask why, and the answer
 * would be different for each of them. So each refusal names which of the four
 * gates the caller is behind, in words that tell them who can open it. That is
 * the difference between a support ticket and a person knowing what to ask for.
 *
 * These states are not error handling bolted on afterwards. They are the
 * screens most likely to be seen, and they are written first.
 *
 * == Frontend visibility is never authorization ==========================
 *
 * Nothing here decides what the caller may see. The shell asks the server what
 * it is allowed to open and renders that answer. A facility absent from the
 * switcher is absent because the server did not name it, and typing its id
 * into the URL produces a 403 from the same gate rather than a page.
 */

import React, { useCallback, useEffect, useRef, useState } from 'react';
import WorkspaceMenu from '../../components/WorkspaceMenu';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { CalendarDays, ClipboardList, LayoutDashboard, Sun, type LucideIcon } from 'lucide-react';
import { WorkspaceLayout, SidebarTrigger, type SidebarGroup } from '../../components/workspaceSidebar';
import { ViewTransition } from '../../components/shadcn/motion';

import Today from './Today';
import FieldWorkspace from './v2/FieldWorkspace';
import FieldSchedule from './v2/FieldSchedule';
import FieldMyDay from './v2/FieldMyDay';
import FieldAtdView from './v2/FieldAtdView';
import FieldTwlPipelineView from './v2/FieldTwlPipelineView';
import { FieldView, isFieldView } from './v2/practices';
import Schedule from './Schedule';
import Manifest, { ManifestSaveOutcome } from './Manifest';
import FeedbackDrawer, { FeedbackButton } from '../../components/feedback/FeedbackDrawer';
import WorkflowPulse from '../../components/feedback/WorkflowPulse';
import { PromptSource } from '../../services/feedbackApi';
import { providerApi, DaySheet, FacilityScope, ProviderFacility, RosterLens, RosterOrder, DEFAULT_LENS, ManifestPatch } from './providerApi';
// 2026-10-21: the worklists include Screened DNQ, which providerApi's RosterLens does not list yet (see manifestLenses.ts).
import { isWorklistLens as isLens } from './v2/manifestLenses';
import { StateBlock as TideState } from '../../components/tide';
// The shared workspace bridge first (Tide phase 2), then this portal's own sheet.
import '../../styles/tide/workspace.css';
import './provider.css';

/**
 * The estate, for a caller whose access is not limited to particular
 * buildings: the ones with a visit coming up first, anything by name on
 * request, and the total so the page reads as a window rather than the
 * whole list. The search runs on the server; this never holds 3,688 rows.
 */
function FacilityPicker({
  facilities, total, query, busy, onSearch, onOpen,
}: {
  facilities: ProviderFacility[]; total: number; query: string; busy: boolean;
  onSearch: (q: string) => void; onOpen: (id: string) => void;
}) {
  // Seeded once from the server's query, never re-synced by an effect: the
  // shell reloads scope on mount and on every search, and an effect syncing
  // this would clear what the caller is halfway through typing. The parent
  // keys this component on the query, so a new result remounts it instead.
  const [draft, setDraft] = useState(query);
  const shown = facilities.length;
  return (
    <section className="pv-card pv-picker" aria-label="Choose a facility">
      <div className="pv-card-head">
        <div>
          <h2>Choose a facility</h2>
          <p className="why">
            Your access is not limited to particular buildings, so this is all {total.toLocaleString()} of them.
            {query ? '' : ' The ones with a visit coming up are first.'}
          </p>
        </div>
      </div>
      <form
        className="pv-search"
        role="search"
        onSubmit={(e) => { e.preventDefault(); onSearch(draft.trim()); }}
      >
        <input
          type="search"
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Search by facility or city"
          aria-label="Search by facility or city"
        />
        <button type="submit" disabled={busy}>Search</button>
        {query && <button type="button" className="pv-clear" onClick={() => { setDraft(''); onSearch(''); }}>Clear</button>}
      </form>
      {shown === 0 ? (
        <TideState kind="empty" reason={query ? 'filtered' : 'none'} message={query ? `No facility matches “${query}”.` : 'No facilities are recorded yet.'} />
      ) : (
        <ul className="pv-facilities">
          {facilities.map((f) => (
            <li key={f.id}>
              <div className="who">
                <strong>{f.name}</strong>
                <span>{[f.city, f.state].filter(Boolean).join(', ') || 'Location not recorded'}{f.region ? ` · ${f.region}` : ''}</span>
              </div>
              <button type="button" className="pv-open" onClick={() => onOpen(f.id)}>Open</button>
            </li>
          ))}
        </ul>
      )}
      {shown > 0 && shown < total && (
        <p className="pv-more">
          Showing {shown} of {total.toLocaleString()}. Search to narrow it.
        </p>
      )}
    </section>
  );
}

type Tab = 'today' | 'manifest' | 'schedule';

const TABS: readonly Tab[] = ['today', 'manifest', 'schedule'];
const TAB_LABELS: Record<Tab, string> = {
  today: 'Today',
  manifest: 'Manifest',
  schedule: 'Schedule',
};

const TAB_ICON: Record<Tab, LucideIcon> = { today: LayoutDashboard, manifest: ClipboardList, schedule: CalendarDays };

/** The sidebar's trigger sits on the dark band of the provider header, so it takes the band's ink. */
const BAND_TRIGGER = '!text-[var(--tide-hero-ink-2)] hover:!bg-white/10 hover:!text-[var(--tide-on-hero)]';

/**
 * The header of a provider screen that has no facility chosen (My day on its own, the facility picker): the
 * sidebar's trigger beside the title, on the same band as the classic header.
 */
function FrameHeader({ title }: { title: string }) {
  return (
    <header className="pv-bar">
      <div className="pv-bar-inner">
        <div className="pv-ident">
          <div className="wsb-title">
            <SidebarTrigger className={BAND_TRIGGER} />
            <div className="pv-where">
              <span className="pv-eyebrow">eMerge · Provider</span>
              <h1>{title}</h1>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}

function isTab(value: string | null): value is Tab {
  return value !== null && (TABS as readonly string[]).includes(value);
}

/**
 * The facility stays in the path because it is the provider's operating scope.
 * The view stays in the query string because it is a presentation of that same
 * day sheet rather than a different resource. Today is canonical without a
 * query so existing provider links remain valid.
 *
 * The manifest's lens rides in the query too (`?lens=enrolled`), so a refresh,
 * Back, a shared link and a feedback record all name the same list. Census is
 * the default and is omitted, so existing `?view=manifest` links are unchanged.
 * A lens is a semantic word, never an identifier: nothing about a resident is
 * ever in a URL.
 */
function classicProviderPath(facilityId: string, tab: FieldView, lens: RosterLens = DEFAULT_LENS, date: string | null = null): string {
  const base = `/provider/${facilityId}`;
  if (tab === 'today') return base;
  const withLens = tab === 'manifest' && lens !== DEFAULT_LENS ? `&lens=${lens}` : '';
  // My day carries its calendar day (a date, never a resident), so Back and shared links name the same day.
  const withDate = tab === 'day' && date ? `&date=${date}` : '';
  return `${base}?view=${tab}${withLens}${withDate}`;
}

/** The shape only, to decide whether to pass the date on; the server is the validator. */
const DAY_SHAPE = /^\d{4}-\d{2}-\d{2}$/;

/**
 * Why the caller cannot see something, in the terms the platform actually
 * uses. `code` comes from the authorization decision; the copy is chosen per
 * code so nobody has to guess which gate they are behind.
 */
interface Refusal {
  title: string;
  body: string;
  detail?: string;
  /** The server was not reached, failed, or answered without the data: danger tone, Try again. Absent = a refusal at a gate. */
  failure?: boolean;
  status?: number;
  /** Not an error: an explanation of a state the person is in (no building assigned yet). Drawn as an empty state. */
  empty?: boolean;
}

const REFUSALS: Record<string, Refusal> = {
  /*
   * Ruling 2026-10-19: a provider's buildings come from facility coverage. Scheduling assigns a provider to cover
   * a building, and the portal access follows once a manager links the provider's login to their provider record.
   * So "no capability" here almost always means "no building assigned to this login yet" -- said as that, not as
   * an error wall or as a role nobody ratified.
   */
  DENY_NO_CAPABILITY: {
    title: 'No facility is assigned to you yet',
    body:
      'Your provider workspace opens on the facilities you are assigned to cover. Scheduling records '
      + 'that assignment in eMerge, and your access follows once your login is linked to your provider '
      + 'record. If you already cover a facility, ask Scheduling to check your assignment and your login link.',
    detail: 'Decision: DENY_NO_CAPABILITY',
    empty: true,
  },
  DENY_SCOPE: {
    title: 'This facility is not one of yours',
    body:
      'Your access is real, but it does not cover this building. If you have started '
      + 'covering it, ask for a facility assignment rather than a new role.',
    detail: 'Decision: DENY_SCOPE',
  },
};

const GENERIC_REFUSAL: Refusal = {
  title: 'You do not have access to this',
  body: 'The server declined this request. If you believe that is wrong, ask an administrator to check your role assignment.',
};

/**
 * The decision the server sent, looked up by its bare name. Authorization V2
 * codes arrive prefixed (`AUTHZ_DENY_NO_CAPABILITY`); the D-04 gate's does
 * not. Before this every V2 refusal fell through to the generic copy, and
 * nobody was told which gate they were behind.
 */
function refusalFor(code: string | undefined | null, status?: number, message?: string): Refusal {
  const bare = String(code || '').replace(/^AUTHZ_/, '');
  if (REFUSALS[bare]) return REFUSALS[bare];
  // Status before the generic copy (2026-10 fix): a 500 or a dropped connection
  // used to read "You do not have access to this", a permissions problem that
  // was not one.
  if (status === undefined && !code) return UNREACHED;
  if (typeof status === 'number' && status >= 500) {
    return { title: 'Server error', body: 'The server failed while loading this. This is not a permissions problem.', failure: true, status };
  }
  if (status === 401 || status === 403) return GENERIC_REFUSAL;
  return { title: 'Not loaded', body: message || 'The server answered without what this screen needs.', failure: true, status };
}


/** A read that never reached the server (no status and no code), or threw before it could. */
const UNREACHED: Refusal = { title: 'Server not reached', body: 'Could not reach the server, so nothing here is current.', failure: true };

/**
 * A refusal or a failure: the shared Tide StateBlock with this portal's own
 * heading, words and detail. A failure is danger with Try again; a refusal (or
 * no building assigned) is the withheld tone. Never an empty building.
 */
function StateBlock({ refusal, retry }: { refusal: Refusal; retry?: () => void }) {
  if (refusal.empty) {
    return (
      <div className="pv-state" role="status">
        <h2 className="pv-state-title">{refusal.title}</h2>
        <TideState kind="empty" reason="none" kindLabel="Not assigned yet" message={refusal.body} />
        {refusal.detail && <p className="pv-state-detail">{refusal.detail}</p>}
      </div>
    );
  }
  return (
    <div className="pv-state">
      <TideState kind="error" heading={refusal.title} headingLevel={2} message={refusal.body} detail={refusal.detail}
        status={refusal.status} tone={refusal.failure ? 'danger' : 'withheld'} onRetry={retry} />
    </div>
  );
}

/*
 * `role="status"` is what makes the loading state discoverable to assistive
 * technology, and `aria-busy` is what would have silenced it: a busy live
 * region is one AT may hold announcements on until it stops being busy, and
 * this one never does — it unmounts. The sr-only text says what the shimmer
 * shows. Where two skeletons stand together only the first speaks, so the
 * provider hears one announcement rather than one per placeholder.
 */
function Skeleton({ announce = true }: { announce?: boolean }) {
  return (
    <section className="pv-card">
      <TideState kind="loading" shape="table" label="Loading provider data…" busy={false} srOnlyLabel announce={announce} />
    </section>
  );
}

const ROSTER_PAGE = 50;

export default function ProviderShell({ fieldWorkspace = false }: { fieldWorkspace?: boolean }) {
  const providerPath = useCallback((id: string, view: FieldView, lens: RosterLens = DEFAULT_LENS, date: string | null = null) => {
    return classicProviderPath(id, view, lens, date);
  }, []);
  const { facilityId } = useParams<{ facilityId?: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const rawView = searchParams.get('view');
  const tab: FieldView = (fieldWorkspace ? isFieldView(rawView) : isTab(rawView)) ? rawView as FieldView : 'today';
  const rawLens = searchParams.get('lens');
  const lens: RosterLens = isLens(rawLens) ? (rawLens as RosterLens) : DEFAULT_LENS;
  const rawDate = searchParams.get('date');
  const day: string | null = tab === 'day' && rawDate && DAY_SHAPE.test(rawDate) ? rawDate : null;

  const [scope, setScope] = useState<FacilityScope | null>(null);
  const [scopeRefusal, setScopeRefusal] = useState<Refusal | null>(null);
  const [scopeLoading, setScopeLoading] = useState(true);

  const [sheet, setSheet] = useState<DaySheet | null>(null);
  const [sheetRefusal, setSheetRefusal] = useState<Refusal | null>(null);
  const [sheetLoading, setSheetLoading] = useState(false);
  const [offset, setOffset] = useState(0);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const sheetRequest = useRef(0);
  useEffect(() => () => { sheetRequest.current += 1; }, []);

  /*
   * The feedback door. One drawer for the whole shell, opened from the
   * utility area or by answering "had friction" to the after-workflow
   * question; the record says which. It is available on the refusal screens
   * too — a provider who was turned away has exactly the friction this
   * exists to hear.
   */
  const [fbOpen, setFbOpen] = useState(false);
  const [fbSource, setFbSource] = useState<PromptSource>('DRAWER');
  const openFeedback = useCallback((source: PromptSource) => { setFbSource(source); setFbOpen(true); }, []);
  const closeFeedback = useCallback(() => setFbOpen(false), []);

  const loadScope = useCallback(async (q?: string) => {
    setScopeLoading(true);
    setScopeRefusal(null);
    try {
      const res = await providerApi.getScope(q);
      if (res.success && res.data) setScope(res.data);
      else setScopeRefusal(refusalFor(res.code, res.status, res.message));
    } catch {
      setScopeRefusal(UNREACHED);
    } finally {
      setScopeLoading(false);
    }
  }, []);

  useEffect(() => { loadScope(); }, [loadScope]);

  /*
   * An unknown view is not a fourth workflow. Normalize it to Today so copied
   * or hand-edited links always land in a state the shell can explain.
   */
  useEffect(() => {
    if (!facilityId || rawView === null || (fieldWorkspace ? isFieldView(rawView) : isTab(rawView))) return;
    navigate(providerPath(facilityId, 'today'), { replace: true });
  }, [facilityId, rawView, navigate, fieldWorkspace, providerPath]);

  // An unknown lens is Census, and the URL is corrected in place so what the
  // address bar says and what the list shows are the same thing.
  useEffect(() => {
    if (!facilityId || rawLens === null || isLens(rawLens)) return;
    navigate(providerPath(facilityId, tab, DEFAULT_LENS, day), { replace: true });
  }, [facilityId, rawLens, tab, day, navigate, providerPath]);

  /*
   * Land on the first facility when the caller has one and named none. A
   * provider covering a single building should not have to choose it. If a
   * valid view was deep-linked at /provider?view=..., preserve that intent.
   *
   * Never for an enterprise caller. Their list is the first page of the
   * whole estate, so "the first one" is whichever building sorts first —
   * a destination nobody chose. They get the picker instead.
   */
  useEffect(() => {
    if (!scope || facilityId || scope.enterprise) return;
    if (scope.facilities.length > 0) {
      // Never land on a building closed for scheduling when an open one exists (verification 2026-10-19).
      const first = scope.facilities.find((f) => !f.closure) || scope.facilities[0];
      navigate(providerPath(first.id, tab, lens, day), { replace: true });
    }
  }, [scope, facilityId, tab, lens, day, navigate, providerPath]);

  // The lens narrows the roster (it lives in the URL); order is how the
  // provider walks the building. Both are server-side (the roster is paged),
  // and both reset the page. Room order is the default: a corridor is not
  // alphabetical.
  const [order, setOrder] = useState<RosterOrder>('room');

  const loadSheet = useCallback(async (id: string, nextOffset: number, l: RosterLens, o: RosterOrder) => {
    const request = ++sheetRequest.current;
    setSheetLoading(true);
    setSheetRefusal(null);
    try {
      const res = await providerApi.getDaySheet(id, { limit: ROSTER_PAGE, offset: nextOffset, lens: l, order: o });
      if (request !== sheetRequest.current) return;
      if (res.success && res.data) {
        setSheet(res.data);
        setLoadedAt(new Date());
      } else {
        setSheet(null);
        setSheetRefusal(refusalFor(res.code, res.status, res.message));
      }
    } catch {
      if (request !== sheetRequest.current) return;
      setSheet(null);
      setSheetRefusal({ ...UNREACHED, title: 'Could not load this facility' });
    } finally {
      if (request === sheetRequest.current) setSheetLoading(false);
    }
  }, []);

  useEffect(() => {
    if (!facilityId) return;
    loadSheet(facilityId, offset, lens, order);
  }, [facilityId, offset, lens, order, loadSheet]);

  const onLens = (l: RosterLens) => { if (!facilityId) return; setOffset(0); navigate(providerPath(facilityId, 'manifest', l)); };
  const onOrder = (o: RosterOrder) => { setOffset(0); setOrder(o); };

  /**
   * Save a resident's provider-owned manifest columns, then re-read the sheet
   * so the row shows what was recorded (workforce cutover, 2026-09-19). The
   * outcome is a sentence for the row, or null when it took.
   */
  const onSaveManifest = async (patientId: string, patch: ManifestPatch): Promise<ManifestSaveOutcome> => {
    if (!facilityId) return 'No facility is open.';
    try {
      const r = await providerApi.updateManifest(facilityId, patientId, patch);
      if (!r.success) {
        if (r.code === 'FIELD_NOT_OWNED_BY_EMERGE') return 'That column is still owned by the scheduling board; it cannot be changed here yet.';
        if (r.code === 'PATIENT_NOT_AT_FACILITY') return 'This resident is no longer confirmed at this building. Reload the manifest.';
        return r.error || 'The change was not recorded.';
      }
      await loadSheet(facilityId, offset, lens, order);
      return null;
    } catch { return 'Could not reach the server. Nothing was changed.'; }
  };

  /** Check in: record the event, then re-read the sheet so the screen shows what was recorded. */
  const onCheckIn = async (contactId: string | null): Promise<boolean> => {
    if (!facilityId) return false;
    try {
      const r = await providerApi.checkIn(facilityId, contactId);
      if (!r.success) return false;
      await loadSheet(facilityId, offset, lens, order);
      return true;
    } catch { return false; }
  };

  // A new building starts on Census: the lens was a question about the
  // building the provider just left.
  const onSwitch = (id: string) => {
    sheetRequest.current += 1;
    setOffset(0);
    setSheet(null);
    setLoadedAt(null);
    navigate(providerPath(id, tab, DEFAULT_LENS, day));
  };

  /** My day: choosing a day changes the address (Back works); null returns to the server's default day. */
  const onSelectDay = (d: string | null) => {
    navigate(facilityId
      ? providerPath(facilityId, 'day', DEFAULT_LENS, d)
      : `/provider?view=day${d ? `&date=${d}` : ''}`);
  };
  /** From a visit card to that facility's rounds. */
  const onOpenRounds = (id: string) => {
    if (id !== facilityId) {
      sheetRequest.current += 1;
      setOffset(0);
      setSheet(null);
      setLoadedAt(null);
    }
    navigate(providerPath(id, 'manifest'));
  };

  // Rendered on every screen below, including the refusals. The route is a
  // template and the view a name: never the URL, which could carry anything.
  const feedback = (
    <FeedbackDrawer
      open={fbOpen}
      onClose={closeFeedback}
      promptSource={fbSource}
      workspace="PROVIDER"
      routeTemplate={facilityId ? '/provider/:facilityId' : '/provider'}
      viewName={tab === 'manifest' ? `manifest:${lens}` : tab}
      facilityId={facilityId ?? null}
    />
  );
  const door = (
    <div className="pv-door">
      <FeedbackButton onClick={() => openFeedback('DRAWER')} />
    </div>
  );

  // ── Refusals and empty scope, before any facility chrome renders ──────

  if (scopeLoading) {
    return <div className="pv"><main className="pv-main"><Skeleton /></main></div>;
  }

  if (scopeRefusal) {
    return (
      <div className="pv">
        <main className="pv-main">
          <StateBlock refusal={scopeRefusal} retry={loadScope} />
          {door}
        </main>
        {feedback}
      </div>
    );
  }

  /*
   * Enterprise scope, and no facility chosen yet: offer the estate as a
   * searchable page rather than a sentence.
   *
   * Until 2026-09-08 this branch was a refusal telling the caller to "open a
   * facility directly by its link" — a link nobody has. So the widest access
   * in the platform produced the narrowest screen, and the operations analyst
   * who designed this workflow could not open the portal she was reviewing.
   * Breadth of access is not a reason to be handed nothing.
   */
  // Without a facility only My day can open (it reads the caller's own visits); the other views need a building.
  // The classic views are all facility-bound, so there the frame carries the workspace header and no items.
  const noFacilityGroups: SidebarGroup[] = fieldWorkspace
    ? [{ key: 'workspace', label: 'Workspace', items: [
      { key: 'day', label: 'My day', icon: Sun, href: '/provider?view=day', active: tab === 'day' },
    ] }]
    : [];

  if (fieldWorkspace && scope && scope.enterprise && !facilityId && tab === 'day') {
    // My day reads the caller's own visits; it needs no facility chosen first.
    return (
      <div className="pv">
        <WorkspaceMenu current="PROVIDER" />
        <WorkspaceLayout workspace="PROVIDER" navLabel="Clinical workspace" groups={noFacilityGroups}>
          <FrameHeader title="My day" />
          <main className="pv-main">
            <div className="fw fw-day-standalone">
              <div className="fw-day-standalone-body">
                <FieldMyDay date={day} onSelectDate={onSelectDay} onOpenFacility={onOpenRounds} />
                <Link to="/provider">Find a facility</Link>
              </div>
            </div>
            {door}
          </main>
        </WorkspaceLayout>
        {feedback}
      </div>
    );
  }

  if (scope && scope.enterprise && !facilityId) {
    return (
      <div className="pv">
        <WorkspaceLayout workspace="PROVIDER" navLabel={fieldWorkspace ? 'Clinical workspace' : 'Provider views'} groups={noFacilityGroups}>
          <FrameHeader title="Select a facility" />
          <main className="pv-main">
            <FacilityPicker
              key={scope.query || ''}
              facilities={scope.facilities}
              total={scope.total ?? scope.facilities.length}
              query={scope.query || ''}
              busy={scopeLoading}
              onSearch={(q) => loadScope(q || undefined)}
              onOpen={(id) => navigate(providerPath(id, 'today'))}
            />
            {door}
          </main>
        </WorkspaceLayout>
        {feedback}
      </div>
    );
  }

  if (scope && !scope.enterprise && scope.facilities.length === 0) {
    // Provisioned but assigned nowhere. This is a different condition from
    // being refused, and conflating the two would send someone hunting for a
    // permissions bug that does not exist.
    return (
      <div className="pv">
        <main className="pv-main">
          <StateBlock refusal={{
            title: 'You are not assigned to a facility yet',
            body:
              'Your access is active, but no building has been assigned to you. A provider '
              + 'sees a facility once someone records that you cover it — ask for a facility '
              + 'assignment, not for more permissions.',
          }}
          />
          {door}
        </main>
        {feedback}
      </div>
    );
  }

  const facilities = scope?.facilities || [];
  const current = facilities.find((f) => f.id === facilityId);

  if (fieldWorkspace && scope && facilityId) {
    // A previous facility's response must never be displayed under the new heading.
    const visibleSheet = sheet?.facility.id === facilityId ? sheet : null;
    return <>
      <WorkspaceMenu current="PROVIDER" />
      <FieldWorkspace
        key={facilityId} sheet={visibleSheet} scope={scope} facilityId={facilityId} view={tab}
        loading={sheetLoading} loadedAt={loadedAt} onView={(view) => navigate(providerPath(facilityId, view, lens))}
        viewHref={(view) => providerPath(facilityId, view, lens)}
        onFacility={onSwitch} onLens={onLens} onOrder={onOrder} onPage={setOffset}
        onRefresh={() => loadSheet(facilityId, offset, lens, order)} onSave={onSaveManifest} onCheckIn={onCheckIn}
        feedback={<FeedbackButton onClick={() => openFeedback('DRAWER')} />}
      >
        {/* Visit plan (ruling 2026-10-19): the covered facilities' schedule is its own read, shown whatever the
            day sheet answers; the facility's logistics (address, contacts, manifest link) follow from the sheet. */}
        {tab === 'day' ? (
          <FieldMyDay facilityId={facilityId} date={day} onSelectDate={onSelectDay} onOpenFacility={onOpenRounds} />
        ) : tab === 'atd-all' ? (
          // ATD across every building the provider may open (2026-10-22); its own read, whatever the day sheet answers.
          <FieldAtdView />
        ) : tab === 'twl' ? (
          // The TWL pipeline across every building the provider may open (2026-10-26); its own read.
          <FieldTwlPipelineView />
        ) : tab === 'schedule' ? (
          <>
            <FieldSchedule facilityId={facilityId} onOpenFacility={onSwitch} />
            {sheetRefusal ? <StateBlock refusal={sheetRefusal} retry={() => loadSheet(facilityId, offset, lens, order)} />
              : !visibleSheet ? <Skeleton />
              : <div className="pv fw-legacy-panel"><Schedule sheet={visibleSheet} /></div>}
          </>
        ) : sheetRefusal ? <StateBlock refusal={sheetRefusal} retry={() => loadSheet(facilityId, offset, lens, order)} />
          : !visibleSheet ? <Skeleton />
          : undefined}
      </FieldWorkspace>
      {feedback}
      {!sheetRefusal && visibleSheet && <WorkflowPulse storageKey={`fb-pulse:PROVIDER:${tab}`} onFriction={() => openFeedback('WORKFLOW_PROMPT')} />}
    </>;
  }


  const classicGroups: SidebarGroup[] = [{
    key: 'views', label: 'Views',
    items: TABS.map((target) => ({
      key: target, label: TAB_LABELS[target], icon: TAB_ICON[target],
      href: facilityId ? providerPath(facilityId, target) : '/provider', active: tab === target,
    })),
  }];

  return (
    <div className="pv">
      <WorkspaceMenu current="PROVIDER" />
      <WorkspaceLayout workspace="PROVIDER" navLabel="Provider views" groups={classicGroups}>
      <header className="pv-bar">
        <div className="pv-bar-inner">
          <div className="pv-ident">
            <div className="wsb-title">
            <SidebarTrigger className={BAND_TRIGGER} />
            <div className="pv-where">
              <span className="pv-eyebrow">eMerge · Provider</span>
              <h1>{current?.name || sheet?.facility.name || 'Select a facility'}</h1>
              {current && (current.city || current.state) && (
                <span className="pv-sub">
                  {[current.city, current.state].filter(Boolean).join(', ')}
                </span>
              )}
            </div>
            </div>

            <div className="pv-aside">
              {facilities.length > 1 && (
                <div className="pv-switch">
                  <label htmlFor="pv-facility">Facility</label>
                  <select
                    id="pv-facility"
                    value={facilityId || ''}
                    onChange={(e) => onSwitch(e.target.value)}
                  >
                    {facilities.map((f) => (
                      <option key={f.id} value={f.id}>{f.name}</option>
                    ))}
                  </select>
                </div>
              )}
              {/* The utility area. Feedback is a door here, not a fourth tab:
                  it is not a workflow destination. */}
              <FeedbackButton onClick={() => openFeedback('DRAWER')} />
            </div>
          </div>

          {/* The workbook links live on the Manifest, where they are opened.
              A link in the shell would follow the provider onto Today and
              Schedule, where neither workbook is what they are looking at.
              The three views are the sidebar's (classicGroups above). */}
        </div>
      </header>

      <main className="pv-main">
        {/* A refused or failed day sheet can be retried in place, as the scope
            can. Without this a connection blip meant reloading the page. */}
        {sheetRefusal && (
          <StateBlock
            refusal={sheetRefusal}
            retry={facilityId ? () => loadSheet(facilityId, offset, lens, order) : undefined}
          />
        )}
        <ViewTransition viewKey={tab} className="flex flex-col gap-[26px]">
          {!sheetRefusal && (sheetLoading && !sheet) && <><Skeleton /><Skeleton announce={false} /></>}
          {!sheetRefusal && sheet && tab === 'today' && (
            <Today sheet={sheet} onPage={setOffset} onCheckIn={onCheckIn} />
          )}
          {!sheetRefusal && sheet && tab === 'manifest' && (
            <Manifest sheet={sheet} onPage={setOffset} onLens={onLens} onOrder={onOrder} stockReportUrl={scope?.links?.stock_report ?? null} onSave={onSaveManifest} />
          )}
          {!sheetRefusal && sheet && tab === 'schedule' && <Schedule sheet={sheet} />}
        </ViewTransition>

        {/* After the work, not before it: the question appears once the
            provider has actually been in this view for a while, once per
            sitting per view, and "had friction" opens the same drawer. */}
        {!sheetRefusal && sheet && facilityId && (
          <WorkflowPulse
            storageKey={`fb-pulse:PROVIDER:${tab}`}
            onFriction={() => openFeedback('WORKFLOW_PROMPT')}
          />
        )}
      </main>
      </WorkspaceLayout>
      {feedback}
    </div>
  );
}
